import { getDefaultOptions } from "../services/Category";
import {
  ApplicationPayload,
  AssignmentScope,
  CoveredPeriod,
  GridProduct,
  ImpactQuery,
  ImpactReport,
  PricingTierApplication,
  PricingTierGrid,
  PricingTierGridPayload,
  ProposedGrid,
  PropertyOption,
  RoundingDirection,
} from "./types";

const request = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(
    new URL(path, window.origin).toString(),
    getDefaultOptions(options)
  );
  if (!response.ok) {
    // The API rejects overlapping assignments and malformed ladders with a
    // message the form has to show verbatim; keep it rather than a generic one.
    const detail = await response.text();
    throw new Error(detail || response.statusText);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
};

interface Paginated<T> {
  count: number;
  results: T[];
}

// `StandardPagination` caps `page_size` at 100, so asking for more is silently
// ignored rather than refused.
const PROPERTY_PAGE_SIZE = 100;

export const createGrid = (body: PricingTierGridPayload) =>
  request<PricingTierGrid>("/api/pricing-tier-grids/", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateGrid = (id: number, body: PricingTierGridPayload) =>
  request<PricingTierGrid>(`/api/pricing-tier-grids/${id}/`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

/**
 * Dry-run everywhere the ladder is already assigned. `offset` and `limit` page
 * the detailed list under the counts; the counts cover the whole scope.
 */
export const fetchImpact = (gridId: number, query: ImpactQuery = {}) =>
  request<ImpactReport>("/api/pricing-tier/impact/", {
    method: "POST",
    body: JSON.stringify({ grid: gridId, ...query }),
  });

/** Put the ladder's prices in place. The prices move in this request. */
export const createApplication = (body: ApplicationPayload) =>
  request<PricingTierApplication>("/api/pricing-tier-applications/", {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * The same dry-run, narrowed to a scope that need not exist yet. It is resolved
 * against the other active assignments, so cells a more specific ladder already
 * drives are left out of the count.
 */
export const fetchActivationImpact = (gridId: number, scope: AssignmentScope) =>
  request<ImpactReport>("/api/pricing-tier/impact/", {
    method: "POST",
    body: JSON.stringify({ grid: gridId, assignment: scope }),
  });

const fetchPropertyPage = (page: number) =>
  request<Paginated<PropertyOption> | PropertyOption[]>(
    `/api/properties/?page_size=${PROPERTY_PAGE_SIZE}&page=${page}` +
      "&ordering=hebergement_nom_court"
  );

/**
 * Every property of the tenant, not the first page of them.
 *
 * A single oversized page came back capped at a hundred rows, which on a
 * tenant with several hundred properties left the rest of the alphabet
 * unpickable. The first page carries the total, so the others are fetched at
 * once rather than one after another.
 */
export const fetchProperties = async (): Promise<PropertyOption[]> => {
  const first = await fetchPropertyPage(1);
  if (Array.isArray(first)) return first;

  const pages = Math.ceil(first.count / PROPERTY_PAGE_SIZE);
  const rest = await Promise.all(
    Array.from({ length: Math.max(pages - 1, 0) }, (_, index) =>
      fetchPropertyPage(index + 2)
    )
  );
  return [first, ...rest].flatMap((payload) =>
    Array.isArray(payload) ? payload : payload.results
  );
};

/** The categories a grid can price for one property, room type included. */
/** What the editor needs to draw a grid for one property. */
interface GridColumns {
  products: GridProduct[];
  /** The increases the decision tree asks for, in percent, ascending. */
  reco_increase_pcts: number[];
}

export const fetchGridProducts = (propertyId: number) =>
  request<GridColumns>(`/api/pricing-tier/columns/?property=${propertyId}`);

/** A reference column stated by its two ends: "79 € to 249 €, 10 levels". */
export const fetchGeneratedLevels = (body: {
  low: string;
  high: string;
  count: number;
  progressive: boolean;
  rounding_direction: RoundingDirection;
  rounding_step: string | null;
  rounding_ending: number | null;
}) =>
  request<{ prices: string[] }>("/api/pricing-tier/generate-levels/", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** A whole matrix read off the hotel's last twelve months. */
export const fetchProposedGrid = (propertyId: number, levels?: number) =>
  request<ProposedGrid>(
    `/api/pricing-tier/proposed-grid/?property=${propertyId}` +
      (levels ? `&levels=${levels}` : "")
  );

/** The tier prices of a calendar window, grouped into the periods a grid covers. */
export const fetchCoveredPeriods = (
  propertyId: number,
  dateFrom: string,
  dateTo: string,
  gridTypeIds: number[]
) =>
  request<{ periods: CoveredPeriod[] }>(
    `/api/pricing-tier/covered-periods/?property=${propertyId}` +
      `&date_from=${dateFrom}&date_to=${dateTo}` +
      gridTypeIds.map((id) => `&grid_types=${id}`).join("")
  );
